import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import { Link, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";
import KeywordsMaker from "../utilities/KeywordsMaker";
import IconButton from "components/common/IconButton";
import PageHeader from "components/common/PageHeader";
import FalconComponentCard from "components/common/FalconComponentCard";
import audiences from "../../config/audiences";
import FileUpload from "components/doc-components/FileUpload";

const EditArticle = () => {
  const navigate = useNavigate();
  const [fileUrlThumb, setFileUrlThumb] = useState(null);
  const [fileUrlImage, setFileUrlImage] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    brief: "",
    content: "",
    credit: "ECPL",
    keyword1: "",
    keyword2: "",
    keyword3: "",
    image_file: null,
    thumb_image_file: null,
    post_date: null,
    featured: false,
    enabled: true,
    audience: "public",
    user_id: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData({
          ...formData,
          user_id: uid,
        });
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const id = window.location.pathname.split("/").pop();

      if (id) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;

          const response = await axios.get(`${apiUrl}/news/id/${id}`);

          if (response.data) {
            // console.log('responded', response.data);
            const eventData = response.data.data[0];
            console.log("eventData", eventData);
            const keyWordsSeparated = eventData.keywords
              ? eventData.keywords.split("; ")
              : ["", "", ""];

            setFormData({
              title: eventData.title,
              brief: eventData.brief,
              content: eventData.content,
              credit: eventData.credit,
              image_file: eventData.image_file,
              thumb_image_file: eventData.thumb_image_file,
              keyword1: keyWordsSeparated[0],
              keyword2: keyWordsSeparated[1],
              keyword3: keyWordsSeparated[2],
              date_added: eventData.date_added,
              post_date: moment
                .utc(eventData.post_date, "YYYY-MM-DD HH:mm")
                .toDate(),
              featured: eventData.featured === "false" ? false : true,
              enabled: eventData.enabled === "false" ? false : true,
              audience: eventData.audience,
              user_id: eventData.user_id,
            });
          }
        } catch (err) {
          console.log("Error", err);
        }
      } else {
        navigate("/news/list");
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form data", formData);

    const keywordsCombined = KeywordsMaker([
      formData.keyword1,
      formData.keyword2,
      formData.keyword3,
    ]);

    console.log("keywordsCombined", keywordsCombined);
    try {
      let formData2 = new FormData();
      formData2.append("title", formData.title);
      formData2.append("brief", formData.brief);
      formData2.append("content", formData.content);
      formData2.append("credit", formData.credit);
      formData2.append("keywords", keywordsCombined);
      formData2.append("date_updated", moment.utc().format("YYYY-MM-DD HH:mm"));
      formData2.append("date_added", formData.date_added);
      formData2.append(
        "post_date",
        formData.post_date &&
          moment.utc(formData.post_date).format("YYYY-MM-DD HH:mm")
      );
      formData2.append("featured", formData.featured);
      formData2.append("enabled", formData.enabled);
      formData2.append(
        "fileUrlThumb",
        fileUrlThumb ?? formData.thumb_image_file
      );
      formData2.append("fileUrlImage", fileUrlImage ?? formData.image_file);
      formData2.append("user_id", formData.user_id);
      formData2.append("audience", formData.audience);

      const apiUrl = `${process.env.REACT_APP_API_URL}`;
      const id = window.location.pathname.split("/").pop();

      const response = await axios.put(`${apiUrl}/news/id/${id}`, formData2);

      if (response) {
        console.log("responded", response.statusText);
        toast.success(`News article updated successfully`, {
          theme: "colored",
        });
        navigate("/news/list");
      }
    } catch (err) {
      console.log("Error", err.message);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "post_date") {
      const dateValue = moment.utc(value).format("YYYY-MM-DD HH:mm");
      setFormData({
        ...formData,
        [name]: dateValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Edit a news article"
        description="This article will be listed on the news page and latest news section on the home page"
        className="mb-3"
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title="Details"
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        <Form onSubmit={handleSubmit}>
          <FalconComponentCard.Body>
            <Form.Label>Title</Form.Label>
            <Form.Control
              size="lg"
              type="text"
              placeholder=""
              name="title"
              onChange={handleFieldChange}
              defaultValue={formData.title}
            />
            <br />
            <Form.Label>Author</Form.Label>
            <Form.Control
              size="lg"
              type="text"
              placeholder=""
              name="credit"
              onChange={handleFieldChange}
              readOnly
              defaultValue={formData.credit}
            />
            <br />
            <Form.Label>Brief</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="brief"
              onChange={handleFieldChange}
              defaultValue={formData.brief}
            />
            <br />
            <Form.Label>Content</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="content"
              onChange={handleFieldChange}
              defaultValue={formData.content}
            />
            <br />

            <Row>
              <Col lg="6">
                <Form.Label>Article thumbnail image</Form.Label>
                <br />
                {formData.thumb_image_file ? (
                  <>
                    <Link to={formData.thumb_image_file} target="_blank">
                      View current thumbnail image
                    </Link>
                    <br />
                    <br />
                  </>
                ) : (
                  <>
                    No thumbnail image
                    <br />
                    <br />
                  </>
                )}
                <FileUpload category={"news"} setFileUrl={setFileUrlThumb} />
              </Col>
              <Col lg="6">
                <Form.Label>Inside article image</Form.Label>
                <br />
                {formData.image_file ? (
                  <>
                    <Link to={formData.image_file} target="_blank">
                      View current inside article image
                    </Link>
                    <br />
                    <br />
                  </>
                ) : (
                  <>
                    No inside article image
                    <br />
                    <br />
                  </>
                )}

                <FileUpload category={"news"} setFileUrl={setFileUrlImage} />
              </Col>
              <Col lang="6">
                <Form.Group
                  controlId="formFileLg"
                  className="mb-3"
                  width="100%"
                >
                  <Form.Label>Post date</Form.Label>
                  <br />
                  <DatePicker
                    selected={formData.post_date}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        post_date: value,
                      })
                    }
                    className="form-control"
                    placeholderText="DD-MM-YYYY H:mm"
                    timeIntervals={60}
                    dateFormat="dd-MM-yyyy H:mm"
                    showTimeSelect
                    fixedHeight
                    width="100%"
                    size="lg"
                    name="post_date"
                    autoComplete="off"
                    required
                    // value={formData.post_date}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg="6">
                <Form.Group>
                  <Form.Label>Audience</Form.Label>
                  <Form.Select
                    size="md"
                    className="mb-3"
                    name="audience"
                    onChange={handleFieldChange}
                    value={formData.audience}
                  >
                    {audiences.map((audience) => (
                      <option key={audience.value} value={audience.value}>
                        {audience.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col lg="6">
                <Form.Group>
                  <Form.Check
                    name="featured"
                    type="checkbox"
                    id="featuredCheckbox"
                    label="Featured"
                    onChange={(e) =>
                      setFormData({ ...formData, featured: e.target.checked })
                    }
                    checked={formData.featured}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Check
                    name="enabled"
                    type="checkbox"
                    id="featuredCheckbox"
                    label="Enabled"
                    onChange={(e) =>
                      setFormData({ ...formData, enabled: e.target.checked })
                    }
                    checked={formData.enabled}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col lg="4">
                <Form.Label>Keyword 1</Form.Label>
                <Form.Control
                  size="lg"
                  type="text"
                  placeholder=""
                  name="keyword1"
                  onChange={handleFieldChange}
                  defaultValue={formData.keyword1}
                />
              </Col>
              <Col lg="4">
                <Form.Label>Keyword 2</Form.Label>
                <Form.Control
                  size="lg"
                  type="text"
                  placeholder=""
                  name="keyword2"
                  onChange={handleFieldChange}
                  defaultValue={formData.keyword2}
                />
              </Col>
              <Col lg="4">
                <Form.Label>Keyword 3</Form.Label>
                <Form.Control
                  size="lg"
                  type="text"
                  placeholder=""
                  name="keyword3"
                  onChange={handleFieldChange}
                  defaultValue={formData.keyword3}
                />
              </Col>
            </Row>

            <Row>
              <Col lg="6" className="mt-4 mb-3 ml-3">
                <Form.Group>
                  <IconButton
                    className="mb-1"
                    variant="falcon-default"
                    size="lg"
                    icon="plus"
                    transform="shrink-3"
                    type="submit"
                  >
                    Submit
                  </IconButton>
                </Form.Group>
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </Form>
      </FalconComponentCard>
    </>
  );
};

export default EditArticle;
